import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import LedgerItemHelper from '../../helpers/LedgerItemHelper'
import moment from '../../utilities/Moment'
import { InvoicesController } from '../../controllers/accountant'
import { AccountantApiError } from '../../controllers/accountant/request'
import NumberFormatter from '../../utilities/NumberFormatter'
import { AccountantWorkspace, LedgerItem } from '../../types'
import ExportInvoicesModal from '../../components/Modals/Accountant/ExportInvoicesModal'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination'
import { DownloadIcon, EllipsisIcon, FileCodeIcon, FileTextIcon } from 'lucide-react'

interface IProps {
  workspace: AccountantWorkspace
  onNotFound: () => void
}

export default function Invoices(props: IProps) {
  const { workspace: { setting } } = props
  const { t } = useTranslation()

  const [ledgerItems, setLedgerItems] = React.useState<LedgerItem[]>([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [perPage] = React.useState(25)
  const [totalPages, setTotalPages] = React.useState(1)
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [showExportInvoicesModal, setShowExportInvoicesModal] = React.useState(false)

  // Ignore responses that arrive after the workspace was switched
  const activeWorkspaceId = React.useRef(props.workspace.id)
  activeWorkspaceId.current = props.workspace.id

  const fetchLedgerItems = (page = 1) => {
    const requestedWorkspaceId = props.workspace.id

    InvoicesController
      .getInvoices(props.workspace.id, page, { per_page: perPage })
      .then(response => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return

        const { current_page, total_pages, invoices: newLedgerItems } = response

        setLedgerItems(newLedgerItems)
        setCurrentPage(current_page)
        setTotalPages(total_pages)
        setDidInitialLoad(true)
      })
      .catch(error => {
        if (activeWorkspaceId.current !== requestedWorkspaceId) return
        if (error instanceof AccountantApiError && error.status === 404) props.onNotFound()
        else console.error(error)
      })
  }

  React.useEffect(() => {
    setDidInitialLoad(false)
    fetchLedgerItems(1)
  }, [props.workspace.id])

  return (
    <>
      <Helmet>
        <title>{'Bizzey | Facturen & Creditnota\'s'}</title>
      </Helmet>
      <ScrollToTopOnMount />

      <div className="flex items-center justify-end mb-4">
        <Button onClick={() => setShowExportInvoicesModal(true)}>
          <DownloadIcon />
          {'Export invoices'}
        </Button>
      </div>

      {!didInitialLoad && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      )}

      {didInitialLoad && ledgerItems.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border py-24 text-center">
          <FileTextIcon className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{'No invoices or creditnotes found'}</p>
        </div>
      )}

      {didInitialLoad && ledgerItems.length > 0 && (
        <div className="rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('LedgerItem::number')}</TableHead>
                <TableHead>{t('LedgerItem::client')}</TableHead>
                <TableHead>{t('LedgerItem::issued_on')}</TableHead>
                <TableHead>{t('LedgerItem::due_on')}</TableHead>
                <TableHead className="text-right">{t('LedgerItem::net_total')}</TableHead>
                <TableHead className="text-right">{t('LedgerItem::amount')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {ledgerItems.map((ledgerItem) => (
                <TableRow key={ledgerItem.id}>
                  <TableCell className="font-medium">
                    {ledgerItem.number || '-'}
                  </TableCell>
                  <TableCell className="max-w-75 truncate">
                    {ledgerItem.contact?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {ledgerItem.issued_on ? moment(ledgerItem.issued_on).format(setting.date_format) : '-'}
                  </TableCell>
                  <TableCell>
                    {ledgerItem.due_on ? moment(ledgerItem.due_on).format(setting.date_format) : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.net_total)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.amount)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
                        <EllipsisIcon />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuGroup>
                          <DropdownMenuLabel>{'Actions'}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => LedgerItemHelper.download(ledgerItem, 'pdf')}>
                            <FileTextIcon />
                            {'Download PDF'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => LedgerItemHelper.download(ledgerItem, 'ubl')}>
                            <FileCodeIcon />
                            {'Download UBL'}
                          </DropdownMenuItem>
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {didInitialLoad && totalPages > 1 && (
        <Pagination className="mt-4 justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage > 1 && fetchLedgerItems(currentPage - 1)
                }}
              />
            </PaginationItem>
            {Array.from({ length: totalPages }).map((_, index) => (
              <PaginationItem key={index}>
                <PaginationLink
                  href="#"
                  isActive={currentPage === index + 1}
                  onClick={(e) => {
                    e.preventDefault()
                    fetchLedgerItems(index + 1)
                  }}
                >
                  {index + 1}
                </PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  currentPage < totalPages && fetchLedgerItems(currentPage + 1)
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <ExportInvoicesModal
        workspaceId={props.workspace.id}
        open={showExportInvoicesModal}
        onOpenChange={setShowExportInvoicesModal}
      />
    </>
  )
}
